import * as React from 'react';
import {Box, Divider, Grid, Slider, Typography, styled, useMediaQuery} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {Stats} from "../types/stats";
import DashboardPanel from "./DashboardPanel";


const NonInteractiveSlider = styled(Slider)(({ theme }) => ({
    '& .MuiSlider-thumb': {
        display: 'none',
    },
    '& .MuiSlider-rail': {
        backgroundColor: theme.palette.grey[300],
    },
    '& .MuiSlider-track': {
        backgroundColor: theme.palette.info.main
    },
    height: 17
}));

export interface StatsViewProps {
    stats: Stats
    ready: boolean
}

export const defaultStats : Stats = {
    allWordsCount: 0,
    learnedWordsCount: 0,
    learnedWordsLastWeekCount: 0,
    completedReviewsCount: 0,
    completedReviewsLastWeekCount: 0,
    currentStreak: 0,
    longestStreak: 0,
    firstDate: new Date(),
    activityToday: false
}

export default function StatsView(
    {
        stats: {
            allWordsCount,
            learnedWordsCount,
            learnedWordsLastWeekCount,
            completedReviewsCount,
            completedReviewsLastWeekCount,
            currentStreak,
            longestStreak,
            firstDate,
            activityToday
        },
        ready
    } : StatsViewProps) {

    const startDate = new Date(firstDate);
    const endDate = new Date();
    const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    const daysSinceStarted = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.only('sm'));

    function simplePaneView(title : string, value : number, fadeValue : boolean = false) {
        return (
            <Box display="flex"
                 justifyContent="space-between"
                 alignItems="center"
                 bgcolor="primary.main"
                 sx={{borderRadius: 2, border: '1px gray solid', pb: 0, mb: 0, mt: 2}}
                 px={{xs: 1, sm: 2}}
            >
                <Typography variant="body1">{title}</Typography>
                <Typography variant="h6" sx={fadeValue ? {color: "gray"} : {}}>{value}</Typography>
            </Box>
        )
    }

    function compoundPaneView(title : string, subTitleLeft : string, valueLeft : number, subTitleRight : string,
                              valueRight : number, fadeValueLeft : boolean = false, fadeValueRight = false,
                              sliderValue?: number) {
        return (
            <Box sx={{border: '1px gray solid', borderRadius: 2, pt: 1, mt: 2}}
                 mx={{xs: 1, sm: 2}}
                 px={{xs: 1, sm: 2}}
                 pb={{xs: sliderValue ? 0 : 2, sm: sliderValue ? 1 : 2}}
            >
                <Typography variant="h6">{title}</Typography>
                <Divider sx={{border: 1}}/>
                <Grid container spacing={2}>
                    <Grid item xs={6}>{simplePaneView(subTitleLeft, valueLeft, fadeValueLeft)}</Grid>
                    <Grid item xs={6}>{simplePaneView(subTitleRight, valueRight, fadeValueRight)}</Grid>
                </Grid>
                {sliderValue !== undefined && (
                    <Box sx={{mb: 0, pb: 0}} mt={{xs: 1, sm: 1}}>
                        <NonInteractiveSlider disabled value={sliderValue} max={1}/>
                    </Box>

                )}
            </Box>
        )
    }

    return (
        <DashboardPanel title="Your Progress" ready={ready} skeletonHeight="100%" height="100%">
            <Box sx={{pt: 2, pb: 2}}>
                <Grid container spacing={1}>
                    <Grid item xs={12} md={12}>
                        {compoundPaneView("Vocabulary progress","Learned", learnedWordsCount, "Total", allWordsCount, false, false,  learnedWordsCount / allWordsCount)}
                    </Grid>
                    <Grid item xs={12} md={12}>
                        {compoundPaneView("Average lessons a day", "Overall", Math.floor(learnedWordsCount / daysSinceStarted), isMobile ? "Week" : "Last 7 days", Math.floor(learnedWordsLastWeekCount / Math.min(daysSinceStarted, 7)))}
                    </Grid>
                    <Grid item xs={12} md={12}>
                        {compoundPaneView("Average reviews a day", "Overall", Math.floor(completedReviewsCount / daysSinceStarted), isMobile ? "Week" : "Last 7 days", Math.floor(completedReviewsLastWeekCount / Math.min(daysSinceStarted, 7)))}
                    </Grid>
                    <Grid item xs={12} md={12}>
                        {compoundPaneView("Day streak", "Current", currentStreak, "Longest", longestStreak, !activityToday)}
                    </Grid>
                </Grid>
            </Box>
        </DashboardPanel>
    );
}
